import { useState } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';

interface Domain {
    id: number;
    domain: string;
    project_id?: number;
    network?: string;
    revshare?: number;
    is_active?: boolean;
}

interface CompactDomainSelectProps {
    value: string[];
    onChange: (val: string[]) => void;
    domains: Domain[] | undefined;
}

export function CompactDomainSelect({ value, onChange, domains }: CompactDomainSelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [lastClickTime, setLastClickTime] = useState<{[key: string]: number}>({});

    // Função para encontrar todos os subdomínios de um domínio
    const getSubdomains = (parentDomain: string) => {
        if (!domains) return [];

        return domains
            .map(d => d.domain)
            .filter(domain => {
                if (domain === parentDomain) return false;

                return domain.endsWith('.' + parentDomain);
            });
    };

    const handleToggleDomain = (domain: string) => {
        const now = Date.now();
        const lastClick = lastClickTime[domain] || 0;
        const timeDiff = now - lastClick;

        // Detecta duplo clique (menos de 300ms entre cliques)
        if (timeDiff < 300) {
            const subdomains = getSubdomains(domain);
            const allDomains = [domain, ...subdomains];

            const allSelected = allDomains.every(d => value.includes(d));

            if (allSelected) {
                onChange(value.filter(d => !allDomains.includes(d)));
            } else {
                const newSelection = [...value];
                allDomains.forEach(d => {
                    if (!newSelection.includes(d)) {
                        newSelection.push(d);
                    }
                });
                onChange(newSelection);
            }
        } else {
            if (value.includes(domain)) {
                onChange(value.filter(d => d !== domain));
            } else {
                onChange([...value, domain]);
            }
        }

        setLastClickTime(prev => ({
            ...prev,
            [domain]: now
        }));
    };

    const handleSelectAll = () => {
        if (domains) {
            onChange(domains.map(d => d.domain));
        }
    };

    const handleClearAll = () => {
        onChange([]);
    };

    const displayText = () => {
        if (value.length === 0) {
            return 'Todos os domínios';
        }
        if (value.length === 1) {
            return value[0];
        }
        if (value.length === domains?.length) {
            return 'Todos os domínios';
        }
        return `${value.length} domínios`;
    };

    // Função para verificar se um domínio tem subdomínios
    const hasSubdomains = (domain: string) => {
        return getSubdomains(domain).length > 0;
    };

    return (
        <div className="relative hidden lg:block">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded-lg hover:border-blue-400 transition-all text-sm min-w-[180px]"
            >
                <Globe className="h-4 w-4 text-gray-500 flex-shrink-0" />
                <span className="font-medium text-gray-700 flex-1 text-left truncate">
                    {displayText()}
                </span>
                {value.length > 0 && value.length < (domains?.length || 0) && (
                    <div className="flex items-center gap-1">
                        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
                            {value.length}
                        </span>
                    </div>
                )}
                <ChevronDown
                    className={`h-3.5 w-3.5 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {isOpen && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
                    <div className="absolute top-full left-0 mt-1 z-50 w-full min-w-[280px] bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
                        {/* Header com ações */}
                        <div className="p-2 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                            <span className="text-xs font-semibold text-gray-600 uppercase">
                                Selecionar Domínios
                            </span>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={handleSelectAll}
                                    className="text-xs text-blue-600 hover:text-blue-700 font-medium px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                                >
                                    Todos
                                </button>
                                <button
                                    onClick={handleClearAll}
                                    className="text-xs text-gray-600 hover:text-gray-700 font-medium px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                                >
                                    Limpar
                                </button>
                            </div>
                        </div>

                        {/* Dica de uso */}
                        <div className="px-3 py-2 bg-blue-50 border-b border-blue-100">
                            <p className="text-xs text-blue-700">
                                💡 <strong>Dica:</strong> Clique 2x em um domínio para selecionar ele + todos os subdomínios
                            </p>
                        </div>

                        {/* Lista de domínios */}
                        <div className="p-1 max-h-64 overflow-y-auto">
                            {domains?.map((domainObj) => {
                                const isSelected = value.includes(domainObj.domain);
                                const hasSubs = hasSubdomains(domainObj.domain);
                                const subdomainsCount = getSubdomains(domainObj.domain).length;

                                return (
                                    <button
                                        key={domainObj.id}
                                        onClick={() => handleToggleDomain(domainObj.domain)}
                                        className={`w-full text-left px-3 py-2 rounded-md transition-colors text-sm flex items-center justify-between group ${
                                            isSelected
                                                ? 'bg-blue-50 text-blue-700'
                                                : 'hover:bg-gray-50 text-gray-700'
                                        }`}
                                    >
                                        <div className="flex-1 flex items-center gap-2">
                                            <span className="truncate">{domainObj.domain}</span>
                                            {hasSubs && (
                                                <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                                                    +{subdomainsCount}
                                                </span>
                                            )}
                                        </div>
                                        {isSelected && (
                                            <Check className="h-4 w-4 text-blue-600 flex-shrink-0" />
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Footer com contador */}
                        {value.length > 0 && (
                            <div className="p-2 border-t border-gray-100 bg-gray-50">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-600">
                                        {value.length} de {domains?.length || 0} selecionados
                                    </span>
                                    <button
                                        onClick={() => setIsOpen(false)}
                                        className="text-xs text-blue-600 hover:text-blue-700 font-medium px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                                    >
                                        Aplicar
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}